/**
 * Button Configuration Section (Shared)
 * Reusable section for configuring action buttons (Reset, Close, Previous, Next, etc.)
 */

import React from 'react';
import Checkbox from 'Components/DesignSystem/Checkbox/Checkbox';
import TextField from 'Components/DesignSystem/TextField/TextField';
import { useDebouncedInput } from '../../../hooks/useDebouncedInput';
import { IconUploadSection } from './IconUploadSection';
import InfoTooltip from 'Components/DesignSystem/Tooltip/InfoTooltip';

export interface ButtonConfigSectionProps {
  buttonName: string;
  showButton: boolean;
  buttonText: string;
  showIcon: boolean;
  iconUrl?: string;
  showTooltip: boolean;
  tooltipText?: string;
  onShowButtonChange: (show: boolean) => void;
  onTextChange: (text: string) => void;
  onShowIconChange: (show: boolean) => void;
  onIconUrlChange: (url: string) => void;
  onIconFileChange?: (file: File | undefined) => void;
  onShowTooltipChange: (show: boolean) => void;
  onTooltipTextChange: (text: string) => void;
  baseId: string;
  disabled?: boolean;
}

export const ButtonConfigSection = ({
  buttonName,
  showButton,
  buttonText,
  showIcon,
  iconUrl,
  showTooltip,
  tooltipText,
  onShowButtonChange,
  onTextChange,
  onShowIconChange,
  onIconUrlChange,
  onIconFileChange,
  onShowTooltipChange,
  onTooltipTextChange,
  baseId,
  disabled = false,
}: ButtonConfigSectionProps) => {
  // Debounced inputs for text fields
  const buttonTextInput = useDebouncedInput(buttonText, onTextChange, 2000);
  const tooltipTextInput = useDebouncedInput(tooltipText || '', onTooltipTextChange, 2000);

  return (
    <div className="tw-space-y-3 tw-p-3 tw-bg-surface-sunken tw-rounded">
      <Checkbox
        label={`Show ${buttonName} Button`}
        checked={showButton}
        onChange={onShowButtonChange}
        disabled={disabled}
      />

      {showButton && (
        <div className="tw-space-y-3 tw-pl-4 tw-border-l-2 tw-border-edge">
          <TextField
            label="Button Text"
            labelSize="sm"
            value={buttonTextInput.localValue}
            onChange={buttonTextInput.handleChange}
            placeholder={`Enter ${buttonName.toLowerCase()} button text`}
            disabled={disabled}
          />

          <div className="tw-flex tw-items-center tw-gap-1">
            <Checkbox
              label="Show Icon"
              checked={showIcon}
              onChange={onShowIconChange}
              disabled={disabled}
            />
            <InfoTooltip
              label="About Show Icon"
              data-testid={`${baseId}-${buttonName.toLowerCase()}-show-icon-info`}
              message="If no icon is uploaded, a default icon will be used"
            />
          </div>

          {showIcon && (
            <IconUploadSection
              label="Button Icon"
              currentUrl={iconUrl || ''}
              onUrlChange={onIconUrlChange}
              onFileChange={onIconFileChange}
              inputId={`${baseId}-${buttonName.toLowerCase()}-icon`}
              disabled={disabled}
            />
          )}

          <Checkbox
            label="Show Tooltip"
            checked={showTooltip}
            onChange={onShowTooltipChange}
            disabled={disabled}
          />

          {showTooltip && (
            <TextField
              label="Tooltip Text"
              labelSize="sm"
              value={tooltipTextInput.localValue}
              onChange={tooltipTextInput.handleChange}
              placeholder="Enter tooltip text"
              disabled={disabled}
            />
          )}
        </div>
      )}
    </div>
  );
};

export default ButtonConfigSection;
